import RouteConstants from '@/features/router/RouteConstants';
import { useModalContext } from '@/hooks/useModalContext';
import { Button, EditIcon, IconButton, Input, PlusIcon, TrashIcon } from '@/lib';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { useEmailTemplates } from '../hooks/useEmailTemplates';
import { EmailTemplate, EmailTemplateStatus } from '../types/emailTemplates';
import { EmailTemplateCard } from './EmailTemplateCard';
import { DeleteEmailTemplateModal } from './DeleteEmailTemplateModal';
import { FILTER_TO_CATEGORY, TemplatesFilterChip, TemplatesViewMode } from './config';
import { EmailTemplateTable } from './EmailTemplateTable';

export const EmailTemplateList: FC = () => {
  const { t } = useTranslation('campaign');
  const history = useHistory();
  const [selectedFilter, setSelectedFilter] = useState<TemplatesFilterChip>('All');
  const [viewMode, setViewMode] = useState<TemplatesViewMode>('cards');
  const [searchValue, setSearchValue] = useState('');
  const { templates, isLoading } = useEmailTemplates();
  const { openModal } = useModalContext();

  const filteredItems = useMemo(() => {
    const searchNormalized = searchValue.trim().toLowerCase();

    return templates.filter((item) => {
      const matchesFilter = selectedFilter === 'All' ? true : item.category === FILTER_TO_CATEGORY[selectedFilter];
      const matchesSearch = searchNormalized.length === 0 ? true : item.name.toLowerCase().includes(searchNormalized);

      return matchesFilter && matchesSearch;
    });
  }, [searchValue, selectedFilter, templates]);

  const templateStats = useMemo(() => {
    const totalByStatus = templates.reduce<Record<EmailTemplateStatus, number>>(
      (acc, item) => {
        acc[item.status] += 1;
        return acc;
      },
      { approved: 0, inReview: 0, rejected: 0, draft: 0 }
    );

    return [
      { key: 'approved', label: 'Approved', value: totalByStatus.approved },
      { key: 'inReview', label: 'In review', value: totalByStatus.inReview },
      { key: 'rejected', label: 'Rejected', value: totalByStatus.rejected },
      { key: 'draft', label: 'Drafts', value: totalByStatus.draft },
    ];
  }, [templates]);

  const handleRequestDeleteTemplate = (template: EmailTemplate) => {
    openModal({
      renderContent: DeleteEmailTemplateModal,
      renderContentProps: {
        template: { id: template.id, name: template.name },
      },
      dismissable: true,
      fullWidth: false,
      fullHeight: false,
    });
  };

  const handleEditTemplate = (templateId: string) => {
    history.push(RouteConstants.editEmailTemplate.replace(':templateId', templateId));
  };

  const handleTemplatesHubEdit = (actionType: 'edit' | 'delete') => {
    if (actionType === 'edit') {
      history.push(RouteConstants.emailTemplatesEditHub);
    } else {
      history.push(RouteConstants.emailTemplatesDeleteHub);
    }
  };

  const templateFilters = useMemo(() => {
    if (templates.length === 0) {
      return [];
    }
    const categories = new Set<TemplatesFilterChip>();
    templates.forEach((template) => {
      const category = Object.entries(FILTER_TO_CATEGORY).find(([, value]) => value === template.category)?.[0] as TemplatesFilterChip;
      if (category) {
        categories.add(category);
      }
    });

    return ['All', ...Array.from(categories)] as TemplatesFilterChip[];
  }, [templates]);

  return (
    <section className='space-y-5 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
      <div className='flex flex-col gap-4 rounded-2xl border border-secondary-border bg-white p-4 lg:flex-row lg:items-start lg:justify-between'>
        <div className='space-y-2'>
          <h2 className='text-2xl font-semibold text-slate-900'>{t('emailWorkspace.templates.templateListTitle')}</h2>
          <p className='max-w-2xl text-sm text-secondary-600'>{t('emailWorkspace.templates.templateListDescription')}</p>
        </div>

        <div className='flex gap-3'>
          <Button type='button' intent='secondary' text='Sync' />
          <IconButton
            leadingIcon={<PlusIcon className='size-5 text-white-100' />}
            className='inline-flex w-full max-w-[220px] justify-center'
            onClick={() => history.push(RouteConstants.createEmailTemplate)}
            text={t('emailWorkspace.templates.createEmailTemplate')}
          />
        </div>
      </div>

      <div className='grid gap-4 rounded-2xl border border-secondary-border bg-white p-4 sm:grid-cols-2 xl:grid-cols-4'>
        {templateStats.map((item) => (
          <div key={item.key} className='space-y-1'>
            <p className='text-[11px] font-semibold uppercase tracking-[0.16em] text-quaternary-500'>
              {(t as (key: string) => string)(`whatsappTemplates.statuses.${item.key}`)}
            </p>
            <p className='text-2xl font-semibold text-slate-900'>{item.value}</p>
          </div>
        ))}
      </div>

      <div className='space-y-3 rounded-2xl border border-secondary-border bg-white p-4'>
        <div className='flex items-center justify-between gap-3'>
          <p className='text-xs font-semibold uppercase tracking-[0.16em] text-quaternary-500'>{t('whatsappTemplates.templateControls')}</p>
          <p className='text-sm text-secondary-600'>
            {filteredItems.length} of {templates.length}
          </p>
        </div>
        <div className='flex flex-col gap-3'>
          <div className='flex flex-row items-center justify-between'>
            <div className='flex flex-wrap gap-2'>
              {templateFilters.map((filter) => (
                <button
                  key={filter}
                  type='button'
                  onClick={() => setSelectedFilter(filter)}
                  className={twMerge(
                    'h-8 rounded-full border px-3 text-xs font-semibold uppercase tracking-[0.12em] border-secondary-border bg-white text-secondary-600 hover:bg-gray-50',
                    selectedFilter === filter && 'border-primary-500 bg-primary-50 text-primary-700'
                  )}
                >
                  {filter}
                </button>
              ))}
            </div>
            <div className='flex items-center gap-2'>
              <IconButton
                type='button'
                intent='secondary'
                className='h-10 w-full shrink-0 justify-center sm:w-auto'
                leadingIcon={<EditIcon className='size-4' />}
                text={t('whatsappTemplates.editTemplatesHub')}
                onClick={() => handleTemplatesHubEdit('edit')}
              />
              <IconButton
                type='button'
                intent='tertiary'
                className='h-10 w-full shrink-0 justify-center border border-red-600 text-red-600 hover:text-red-800 sm:w-auto'
                leadingIcon={<TrashIcon className='size-4' />}
                text={t('whatsappTemplates.deleteTemplatesHub')}
                onClick={() => handleTemplatesHubEdit('delete')}
              />
            </div>
          </div>

          <div className='flex w-full items-center justify-between'>
            <Input
              type='text'
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              placeholder={t('whatsappTemplates.searchPlaceholder')}
              className='h-10 border-secondary-border bg-white text-sm text-secondary-500 sm:w-[280px]'
            />
            <div className='flex items-center gap-2'>
              <button
                type='button'
                onClick={() => setViewMode('cards')}
                className={twMerge(
                  'h-10 rounded-lg border border-secondary-border bg-white px-3 text-xs font-semibold uppercase tracking-[0.12em] text-secondary-600 hover:bg-gray-50',
                  viewMode === 'cards' && 'border-primary-500 bg-primary-50 text-primary-700'
                )}
              >
                {t('whatsappTemplates.cardsView')}
              </button>
              <button
                type='button'
                onClick={() => setViewMode('table')}
                className={twMerge(
                  'h-10 rounded-lg border border-secondary-border bg-white px-3 text-xs font-semibold uppercase tracking-[0.12em] text-secondary-600 hover:bg-gray-50',
                  viewMode === 'table' && 'border-primary-500 bg-primary-50 text-primary-700'
                )}
              >
                {t('whatsappTemplates.tableView')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className='rounded-2xl border border-secondary-border bg-white p-4 text-sm text-secondary-600'>{t('whatsappTemplates.loadingTemplates')}</div>
      ) : null}

      {!isLoading && viewMode === 'cards' ? (
        <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
          {filteredItems.map((item) => (
            <EmailTemplateCard key={item.id} template={item} onEdit={handleEditTemplate} onRequestDelete={handleRequestDeleteTemplate} />
          ))}
        </div>
      ) : null}

      {!isLoading && viewMode === 'table' ? (
        <div className='overflow-x-auto rounded-2xl border border-secondary-border bg-white'>
          <EmailTemplateTable items={filteredItems} onEdit={handleEditTemplate} onRequestDelete={handleRequestDeleteTemplate} />
        </div>
      ) : null}

      {!isLoading && filteredItems.length === 0 ? (
        <div className='rounded-2xl border border-dashed border-secondary-border bg-white p-5 text-sm text-secondary-600'>
          {searchValue.trim().length > 0 || selectedFilter !== 'All'
            ? 'No templates match your current filters. Try clearing search or changing category.'
            : t('whatsappTemplates.noTemplatesFound')}
        </div>
      ) : null}
    </section>
  );
};
